import { useNavigate } from '@tanstack/react-router'
import { trpc } from '@viastud/ui/lib/trpc'
import { type Grade, type Subject } from '@viastud/utils'
import { useMemo, useState } from 'react'

import { useAuthStore } from '@/store/auth.store'

import type { BookingChapter } from '../components/booking/booking-card.tsx'

export type ViewMode = 'grid' | 'list'

export function useBookingPresenter() {
  const { user: student } = useAuthStore()
  const navigate = useNavigate()

  // State
  const [searchValue, setSearchValue] = useState('')
  const [selectedGrade] = useState<Grade>(student?.grade ?? 'TROISIEME')
  const [selectedSubject, setSelectedSubject] = useState<Subject | 'ALL'>('MATHS')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [selectedModuleIds, setSelectedModuleIds] = useState<Record<number, number>>({})

  // Queries
  const { data: modules, isLoading } = trpc.module.getAllWithSheets.useQuery({
    grade: selectedGrade,
  })
  const { data: popularModules } = trpc.module.getPopularModules.useQuery({
    grade: selectedGrade,
  })
  const { data: tokenInfo } = trpc.user.getTokenBalance.useQuery()

  // Group modules by chapter
  const chapters = useMemo(() => {
    if (!modules) return []

    const chapterMap = new Map<number, BookingChapter>()

    for (const module of modules) {
      if (selectedSubject !== 'ALL' && module.subject !== selectedSubject) continue
      if (
        searchValue &&
        !module.name.toLowerCase().includes(searchValue.toLowerCase()) &&
        !module.chapter.name.toLowerCase().includes(searchValue.toLowerCase())
      )
        continue

      const chapterId = module.chapter.id
      const sheet = module.sheets.standardSheet
      if (!chapterMap.has(chapterId)) {
        if (
          !tokenInfo?.hasSignUpBonus &&
          module.chapter.name.toLowerCase().includes("cours d'essai")
        ) {
          continue
        }
        chapterMap.set(chapterId, {
          id: chapterId,
          name: module.chapter.name,
          imageUrl: module.chapter.imageUrl ?? null,
          grade: module.grade,
          subject: module.subject,
          duration: module.grade === 'TERMINALE' || module.grade === 'PREMIERE' ? '1h30' : '1h',
          modules: [],
          order: module.chapter.order,
        })
      }

      chapterMap.get(chapterId)?.modules.push({
        id: Number(sheet.id),
        name: module.name,
        chapterNumber: module.chapter.name.split(':')?.at(0) ?? '1',
      })
    }

    return Array.from(chapterMap.values()).sort((a, b) => a.order - b.order)
  }, [modules, selectedSubject, searchValue, tokenInfo?.hasSignUpBonus])

  // Recommendations Logic
  const { data: studentProfileData } = trpc.user.getStudentProfileData.useQuery(undefined, {
    enabled: !!student,
  })

  const selectedForYou = useMemo(() => {
    if (!studentProfileData?.moduleProgress || chapters.length === 0) return []

    // Find the last module followed (doing or the last done)
    const activeProgress =
      studentProfileData.moduleProgress.find((p) => p.doing) ??
      [...studentProfileData.moduleProgress].reverse().find((p) => p.done)

    const testChapter = chapters.find((chapter) =>
      chapter.name.toLowerCase().includes("cours d'essai")
    )

    if (!activeProgress) {
      const result = testChapter ? [testChapter] : []
      const otherChapters = chapters.filter((c) => c.id !== testChapter?.id)
      result.push(...otherChapters.slice(0, 4 - result.length))
      return result
    }

    const currentChapterIndex = chapters.findIndex((c) => c.name === activeProgress.chapterName)

    if (currentChapterIndex === -1) {
      const result = testChapter ? [testChapter] : []
      const otherChapters = chapters.filter((c) => c.id !== testChapter?.id)
      result.push(...otherChapters.slice(0, 4 - result.length))
      return result
    }

    const result: BookingChapter[] = []
    if (testChapter) result.push(testChapter)

    const otherChapters: BookingChapter[] = []
    // If it's the first chapter, show 1 and 2
    if (currentChapterIndex === 0) {
      otherChapters.push(chapters[0])
      if (chapters[1]) otherChapters.push(chapters[1])
    } else {
      // Show previous, current, next
      if (chapters[currentChapterIndex - 1]) otherChapters.push(chapters[currentChapterIndex - 1])
      otherChapters.push(chapters[currentChapterIndex])
      if (chapters[currentChapterIndex + 1]) otherChapters.push(chapters[currentChapterIndex + 1])
    }

    // Filter out test chapter from other chapters if it was already added to result
    const filteredOtherChapters = otherChapters.filter((c) => c.id !== testChapter?.id)
    result.push(...filteredOtherChapters)

    // If we still have space, add more chapters from the beginning (excluding those already in result)
    if (result.length < 4) {
      const remainingChapters = chapters.filter((c) => !result.some((r) => r.id === c.id))
      result.push(...remainingChapters.slice(0, 4 - result.length))
    }

    return result.slice(0, 4)
  }, [chapters, studentProfileData])

  // Popular Logic (Mocked for now or based on some criteria)
  const popularChapters = useMemo(() => {
    if (!chapters || !popularModules) return []
    const testChapter = chapters.find((chapter) =>
      chapter.name.toLowerCase().includes("cours d'essai")
    )

    const result = chapters
      .filter((c) => popularModules.some((p) => p.chapterId === c.id))
      .filter((c) => c.id !== testChapter?.id)
    if (result.length < 6) {
      const chapterNotInResult = chapters
        .filter((c) => c.id !== testChapter?.id)
        .filter((c) => c.id !== result.find((r) => r.id === c.id)?.id)
      return result.concat(
        chapterNotInResult.slice(0, 6 - result.length).sort(() => Math.random() - 0.5)
      )
    }
    return result
  }, [chapters, popularModules])

  // Actions
  const handleModuleChange = (chapterId: number, moduleId: number) => {
    setSelectedModuleIds((prev) => ({ ...prev, [chapterId]: moduleId }))
  }

  const handleBook = (chapterId: number, moduleId: number) => {
    void navigate({
      to: `/ressources/${selectedGrade.toLowerCase()}/${selectedSubject.toLowerCase()}/${chapterId}/${moduleId}`,
      search: { book: true },
    })
  }

  return {
    searchValue,
    setSearchValue,
    selectedGrade,
    selectedSubject,
    setSelectedSubject,
    viewMode,
    setViewMode,
    chapters,
    selectedForYou,
    popularChapters,
    selectedModuleIds,
    handleModuleChange,
    handleBook,
    isLoading,
  }
}
